import React, { useState, useEffect } from 'react';
import { projectAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Users, Plus, Trash2, Mail, Shield, Crown, Code2, Palette, 
  TestTube, FileText, Eye, X, Check, UserPlus
} from 'lucide-react';
import { toast } from 'sonner';

const roleConfig = {
  owner: { label: 'Owner', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20', icon: Crown, description: 'Full access to all features' },
  admin: { label: 'Admin', color: 'bg-purple-500/10 text-purple-400 border-purple-500/20', icon: Shield, description: 'Can manage members and settings' },
  developer: { label: 'Developer', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20', icon: Code2, description: 'Code, architecture, testing access' },
  designer: { label: 'Designer', color: 'bg-pink-500/10 text-pink-400 border-pink-500/20', icon: Palette, description: 'Design phase access only' },
  qa: { label: 'QA', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', icon: TestTube, description: 'Testing and bug tracking access' },
  ba: { label: 'Business Analyst', color: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20', icon: FileText, description: 'Requirements and documentation' },
  viewer: { label: 'Viewer', color: 'bg-slate-500/10 text-slate-400 border-slate-500/20', icon: Eye, description: 'Read-only access' },
};

const rolePermissions = {
  owner: ['all'],
  admin: ['requirements', 'design', 'architecture', 'domain', 'technology', 'code', 'tests', 'tasks', 'bugs', 'roadmap', 'members', 'settings'],
  developer: ['requirements', 'architecture', 'domain', 'technology', 'code', 'tests', 'tasks', 'bugs'],
  designer: ['requirements', 'design', 'tasks'],
  qa: ['requirements', 'tests', 'tasks', 'bugs'],
  ba: ['requirements', 'design', 'tasks', 'roadmap'],
  viewer: ['view_only'],
};

export default function MembersPanel({ projectId, currentUserId, isOwner, onMembersChange }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('developer');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    loadMembers();
  }, [projectId]);

  const loadMembers = async () => {
    try {
      const res = await projectAPI.listMembers(projectId);
      setMembers(res.data);
      if (onMembersChange) onMembersChange(res.data);
    } catch (err) {
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Email is required');
      return;
    }
    if (!inviteEmail.includes('@')) {
      toast.error('Invalid email format');
      return;
    }
    setAdding(true);
    try {
      await projectAPI.addMember(projectId, { email: inviteEmail, role: inviteRole });
      setInviteEmail('');
      setInviteRole('developer');
      setShowAddForm(false);
      loadMembers();
      toast.success('Member added successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add member');
    } finally {
      setAdding(false);
    }
  };

  const handleUpdateRole = async (memberId, newRole) => {
    try {
      await projectAPI.updateMemberRole(projectId, memberId, newRole);
      loadMembers();
      toast.success('Role updated');
    } catch (err) {
      toast.error('Failed to update role');
    }
  };

  const handleRemoveMember = async (memberId) => {
    try {
      await projectAPI.removeMember(projectId, memberId);
      loadMembers();
      toast.success('Member removed');
    } catch (err) {
      toast.error('Failed to remove member');
    }
  };

  const getMemberStats = () => {
    const stats = {};
    members.forEach(m => {
      stats[m.role] = (stats[m.role] || 0) + 1;
    });
    return stats;
  };

  const stats = getMemberStats();

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin h-7 w-6 border-2 border-[var(--accent-indigo)] border-t-transparent rounded-full"></div></div>;
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-[var(--surface-border)] flex items-center gap-2 flex-wrap">
        <Badge className="bg-[var(--accent-indigo)]/10 text-[var(--accent-indigo)] border-[var(--accent-indigo)]/20 text-xs">
          <Users className="w-4 h-4 mr-1" /> Team Members
        </Badge>
        
        <div className="flex items-center gap-2 ml-2">
          <div className="flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-[var(--surface-card)] border border-[var(--surface-border)]">
            <Users className="w-4 h-4 opacity-40" />
            <span className="font-medium">{members.length}</span>
            <span className="opacity-50">members</span>
          </div>
        </div>
        
        <div className="flex-1" />
        
        {isOwner && (
          <Button size="sm" className="h-8 text-xs bg-[var(--accent-indigo)] hover:bg-[var(--accent-indigo)]/90 text-white" onClick={() => setShowAddForm(true)} data-testid="add-member-btn">
            <UserPlus className="w-4 h-4 mr-1" /> Invite Member
          </Button>
        )}
      </div>

      {/* Add Member Form */}
      {showAddForm && (
        <div className="p-3 border-b border-[var(--surface-border)] bg-[var(--surface-card)]">
          <div className="flex items-end gap-3 max-w-xl">
            <div className="flex-1">
              <Label className="text-xs mb-1 block opacity-60">Email Address</Label>
              <Input 
                value={inviteEmail} 
                onChange={(e) => setInviteEmail(e.target.value)} 
                placeholder="colleague@company.com" 
                className="h-8 text-sm bg-transparent border-[var(--surface-border)]" 
                data-testid="invite-email"
              />
            </div>
            <div className="w-40">
              <Label className="text-xs mb-1 block opacity-60">Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger className="h-8 text-xs bg-transparent border-[var(--surface-border)]" data-testid="invite-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[var(--surface-bg)] border-[var(--surface-border)]">
                  {Object.entries(roleConfig).filter(([k]) => k !== 'owner').map(([key, config]) => (
                    <SelectItem key={key} value={key} className="text-xs">
                      <div className="flex items-center gap-2">
                        <config.icon className="w-4 h-4" />
                        {config.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button size="sm" className="h-8 text-xs bg-[var(--accent-indigo)] hover:bg-[var(--accent-indigo)]/90 text-white" onClick={handleAddMember} disabled={adding} data-testid="invite-submit">
              {adding ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
              Add
            </Button>
            <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setShowAddForm(false)} data-testid="invite-cancel">
              <X className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-xs opacity-40 mt-2">Note: User must be registered to be added as a member.</p>
        </div>
      )}

      <ScrollArea className="flex-1">
        <div className="p-3 space-y-3">
          {/* Role Legend */}
          <div className="bg-[var(--surface-card)] rounded-lg border border-[var(--surface-border)] p-3">
            <p className="text-xs font-medium opacity-40 uppercase tracking-wider mb-2">Role Permissions</p>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(roleConfig).map(([key, config]) => {
                const Icon = config.icon;
                return (
                  <div key={key} className="flex items-start gap-2 p-2 rounded bg-[var(--surface-bg)] border border-[var(--surface-border)]">
                    <div className={`w-6 h-7 rounded flex items-center justify-center shrink-0 ${config.color.split(' ')[0]}`}>
                      <Icon className={`w-4 h-4 ${config.color.split(' ')[1]}`} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium">{config.label}</p>
                      <p className="text-xs opacity-50">{config.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Members List */}
          <div className="bg-[var(--surface-card)] rounded-lg border border-[var(--surface-border)] overflow-hidden">
            <div className="px-4 py-3 border-b border-[var(--surface-border)] bg-[var(--surface-bg)]">
              <p className="text-xs font-medium">Team Members ({members.length})</p>
            </div>
            <div className="divide-y divide-[var(--surface-border)]">
              {members.map(member => {
                const config = roleConfig[member.role] || roleConfig.viewer;
                const Icon = config.icon;
                const isCurrentUser = member.user_id === currentUserId;
                const canEdit = isOwner && member.role !== 'owner';
                
                return (
                  <div key={member.id} className="p-3 flex items-center gap-3 hover:bg-[var(--surface-bg)]/50" data-testid={`member-row-${member.id}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${config.color.split(' ')[0]}`}>
                      <Icon className={`w-5 h-5 ${config.color.split(' ')[1]}`} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate">{member.name || 'Unknown'}</p>
                        {isCurrentUser && <Badge variant="outline" className="text-xs h-4">You</Badge>}
                      </div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <Mail className="w-4 h-4 opacity-30" />
                        <span className="text-xs opacity-50 truncate">{member.email}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {canEdit ? (
                        <Select value={member.role} onValueChange={(v) => handleUpdateRole(member.id, v)}>
                          <SelectTrigger className="h-8 text-xs w-32 bg-transparent border-[var(--surface-border)]" data-testid={`role-select-${member.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[var(--surface-bg)] border-[var(--surface-border)]">
                            {Object.entries(roleConfig).filter(([k]) => k !== 'owner').map(([key, cfg]) => (
                              <SelectItem key={key} value={key} className="text-xs">
                                <div className="flex items-center gap-2">
                                  <cfg.icon className="w-4 h-4" />
                                  {cfg.label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Badge className={`text-xs ${config.color}`}>
                          <Icon className="w-4 h-4 mr-1" /> {config.label}
                        </Badge>
                      )}
                      
                      {canEdit && (
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-7 opacity-30 hover:opacity-100 hover:text-red-400" 
                          onClick={() => handleRemoveMember(member.id)}
                          data-testid={`remove-member-${member.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Role Stats */}
          <div className="bg-[var(--surface-card)] rounded-lg border border-[var(--surface-border)] p-3">
            <p className="text-xs font-medium opacity-40 uppercase tracking-wider mb-2">Team Composition</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats).map(([role, count]) => {
                const config = roleConfig[role];
                if (!config) return null;
                return (
                  <Badge key={role} className={`text-xs ${config.color}`}>
                    {config.label}: {count}
                  </Badge>
                );
              })}
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
